import { useId, useMemo } from 'react';
import { Link } from 'react-router';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    EllipsisIcon,
    PencilIcon,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import type { MeUser } from '~/server/types';
import { useNotifications } from '~/modules/notifications';
import { useChatBoxes } from '../../hooks';
import { DRAWER_HEADER_HEIGHT_REM } from '../../utils';
import { getInboxURL } from '../../utils/url';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { NotificationBadge } from '../NotificationBadge';

/**
 * The header of the chat inbox drawer.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxDrawerHeader({
    user,
    professionalID,
}: {
    /**
     * The current logged-in user.
     */
    user: MeUser;
    /**
     * The professional ID of the company inbox to display. If `null`, the
     * personal inbox will be displayed.
     */
    professionalID: number | null;
}) {
    const id = useId();
    const { chatInboxState, setChatInboxState, openChatBox } = useChatBoxes();
    const { notifications } = useNotifications();
    const professional = useMemo(() => {
        if (professionalID) {
            return (
                user.Professionals.find(
                    (professional) => professional.ID === professionalID,
                ) ?? null
            );
        }
        return null;
    }, [professionalID, user]);
    const participant = professional ?? user;

    return (
        <header
            // `shrink-0`: the drawer is a flex column, and when it is cropped
            // to this header — closed, as a floating island — a shrinkable
            // header gives up height, squashing the avatar and letting the
            // separator below it show inside the island.
            className="flex shrink-0 cursor-pointer items-center gap-3 rounded-[inherit] px-3 select-none hover:bg-gray-100"
            style={{ height: `${DRAWER_HEADER_HEIGHT_REM}rem` }}
            aria-labelledby={id}
            onClick={() => {
                if (chatInboxState === 'open') {
                    setChatInboxState('closed');
                } else {
                    setChatInboxState('open');
                }
            }}
        >
            <ParticipantAvatar
                className="size-9"
                fallbackClassName="text-4.5!"
                participant={participant}
                activeStatus={
                    participant.__typename === 'Me' ? 'active' : undefined
                }
            />
            <div
                id={id}
                className="min-w-0 flex-1 text-4.5 leading-none font-medium"
            >
                Messaging
            </div>
            <NotificationBadge count={notifications.totalUnreadCount} />
            <div className="flex shrink-0 items-center gap-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className="grid size-9 place-items-center rounded-full hover:bg-black/10"
                            aria-label="More options"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <EllipsisIcon className="size-5 text-black/70" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-unset min-w-32"
                        side={chatInboxState === 'open' ? 'bottom' : 'top'}
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DropdownMenuItem
                            className="focus:bg-black/10"
                            onClick={() => setChatInboxState('hidden')}
                        >
                            Hide
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="focus:bg-black/10">
                            <Link
                                to={getInboxURL(
                                    professional?.URLSegment ?? null,
                                )}
                            >
                                Full-page inbox
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="focus:bg-black/10">
                            <Link to="/app/settings/messaging">Settings</Link>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <button
                    className="grid size-9 place-items-center rounded-full hover:bg-black/10"
                    aria-label="New Message"
                    onClick={(e) => {
                        e.stopPropagation();
                        openChatBox(0, null);
                    }}
                >
                    <PencilIcon className="size-5 text-black/70" />
                </button>
                <button
                    className="grid size-9 place-items-center rounded-full hover:bg-black/10"
                    aria-label="Toggle chat inbox"
                >
                    {chatInboxState === 'open' ? (
                        <ChevronDownIcon className="size-5 text-black/70" />
                    ) : (
                        <ChevronUpIcon className="size-5 text-black/70" />
                    )}
                </button>
            </div>
        </header>
    );
}
